<!--

============================================================================================================
||    Filter component                                                                                    ||
============================================================================================================

|   Props
------------------------------------------------------------------------------------------------------------
  - asyncMode : TODO
  - filterFn : Function to filter the items on the list.
  - items : Items to be populated in te list of options.
  - label : Key to use for the label on item if item is provided as object.
  - minItemsToShowSearch : Define minimum number of items before the search bar is shown
  - searchTerm : Value for the search to be prepopulated with.
  - searchTermPlaceholder : Search input placeholder.
  - showLoader : In case of async filter this can force the loader to be shown on the bottom. TODO
  - showPrefixIcon : Flag for showing the prefix icon.
  - showSuffixIcon : Flag for showing the suffix icon.
  - subLabel : Key to use for the sublabel on item if item is provided as object.
  - title : Title for the dropdown menu.
  - trackBy : Key to use for the value to be included in the selected items response array.
  - trackIcon : Key to use for the item icon on item if item is provided as object.
  - showPrefixTick : Flag for showing the prefix tick if item is selected.
  - showSuffixTick : Flag for showing the suffix tick if item is selected.
  - value : v-model value bind

|   Events
------------------------------------------------------------------------------------------------------------
  - search-for - triggered when search input is changed
  - item updated - triggered on saved
  - items-selected - triggered on saved
  - input - triggered for v-model bind on save
  - item-selected - triggered on select
  - item-removed - triggered on select
  - items-removed - triggered on cancel
  - items-readded - triggered on cancel

|   Slots
------------------------------------------------------------------------------------------------------------
  - default(searchTerm, item, index) - located inside li item and it will override the default li view

|   Features
------------------------------------------------------------------------------------------------------------
  - Search bar searches through items or label and sublabel if it is defined
  - No search results message
  - Button is disabled if there are no items to filter
  - Button shows number of selected items from the filter if at least one item is selected
  - Resets the selection if closed but not saved
  - Scrolls an item into view if hovered and it is partially out of view
  - Default slot
    - prefix tick
    - prefix icon
    - item icon
    - item label
    - item sublabel
    - suffix icon
    - suffix tick

|   TODO
------------------------------------------------------------------------------------------------------------
  - Change component to implement dropdown from bootstrap for better positioning
  - detect overflow and change position - see if this can help - https://popper.js.org/docs/v2/utils/detect-overflow/
  - detect screen size and adapt view e.g. on mobile show filter as full screen
  - button title as prop
  - cancel text as prop
  - save text as prop
  - enable input items as groups - then remove groupsTitle prop
  - prefix icon as prop
  - suffix icon as prop
  - async mode - spinner already in place
  - integration emojis should not be a part of final version. This should be done through slots or something else.

-->

<template>
  <div id="filter-wrapper">

    <!-- FILTER BUTTON -->
    <b-button
      ref="button"
      class="filter-button flex center-all sb2-bordered sb2-hoverable sb2-focusable"
      :class="{'counter-present': selectedItemsCount}"
      :disabled="items.length === 0"
      @click="toggleFilter">
      <SBIcon
        class="filter-icon"
        :icon="SBIconList.FILTER"
        :color="SBColors.blackPrimary"
        :size="12" />
      <span>Filter</span>
      <span
        v-if="selectedItemsCount"
        class="filter-count">
        {{ selectedItemsCount }}
      </span>
    </b-button>

    <!-- FILTER DROPDOWN MENU -->
    <div
      v-if="filterVisible"
      v-click-outside="closeFilter"
      class="filter-dropdown flex column"
      :class="[placement]">

      <!-- HEADER -->
      <header class="flex center-all">
        <b-button
          class="flex center-all"
          @click="clearFilter">
          <span>Clear</span>
        </b-button>
        <span class="title greedy flex center-all">{{ title }}</span>
        <b-button
          variant="primary"
          class="flex center-all"
          @click="saveFilter">
          <span>Save</span>
        </b-button>
      </header>

      <!-- SEARCH -->
      <section
        v-if="items.length > minItemsToShowSearch"
        class="search-input-wrapper">
        <b-form-input
          ref="searchTermInputRef"
          v-model="searchTermValue"
          class="sb2-bordered sb2-hoverable sb2-focusable"
          type="text"
          :placeholder="searchTermPlaceholder"/>
        <SBIcon
          v-if="searchTermValue"
          class="clear-input flex flex-align-center"
          :icon="SBIconList.CLOSE_THIN"
          :color="SBColors.blackUltralight"
          :hover-icon-color="SBColors.blackPrimary"
          :size="8"
          :hover-size="16"
          :pointer="true"
          @click="clearSearchTerm" />
      </section>

      <!-- FILTERED LIST ITEMS -->
      <ScrollArea
        :bottom-offset="100"
        @bottom="handleBottomScroll">
        <div
          v-if="searchTermValue && searchResults.length === 0 && !showLoader"
          class="no-match flex center-all" >
          <span class="emoji-fix">😐</span>
          <p>We couldn't find anything for <b>{{ searchTermValue }}</b>.</p>
        </div>
        <template v-else>
          <h5 class="group-title">{{ groupTitle }}</h5>
          <ul
            ref="itemListWrapperRef"
            class="filter-list">
            <li
              v-for="(item, index) in searchResults"
              :key="index"
              class="flex flex-align-center"
              @click="selectItem(item)"
              @mouseover="itemHovered(index)"
              @mouseout="deselect()" >
              <slot
                :searchTerm="searchTermValue"
                :item="item"
                :index="index">
                <!-- DEFAULT list item -->
                <span class="default-li flex flex-align-center">
                  <!-- DEFAULT prefix tick -->
                  <SBIcon
                    v-if="showPrefixTick && (item[trackBy] ? selectedItems[item[trackBy]] : selectedItems[item])"
                    :icon="SBIconList.DONE_TICK"
                    :color="SBColors.bluePrimary"
                    :size="9" />
                  <!-- DEFAULT prefix icon -->
                  <SBIcon
                    v-if="showPrefixIcon"
                    class="prefix-icon flex flex-align-center rotate-270"
                    :icon="SBIconList.DOWN_ARROW"
                    :color="SBColors.blackUltralight"
                    :hover-icon-color="SBColors.blackPrimary"
                    :size="8"
                    :hover-size="16" />
                  <!-- DEFAULT item icon -->
                  <div
                    v-if="trackIcon && item[trackIcon]"
                    class="no-shrink item-icon-wrapper">
                    <span class="item-icon">{{ item[trackIcon] }}</span>
                  </div>
                  <div
                    v-else-if="item.integration"
                    class="no-shrink item-icon-wrapper">
                    <span
                      class="item-icon"
                      :class="[item.integration]" />
                  </div>
                  <div
                    v-else-if="item.integrationCount"
                    class="no-shrink item-icon-wrapper">
                    <span class="item-icon" >{{ item.integrationCount }}</span>
                  </div>
                  <!-- DEFAULT item text -->
                  <div class="greedy ellipsis content flex column space-between">
                    <h3
                      class="ellipsis"
                      :inner-html.prop="(item[label] ? item[label] : item) | boldMatch(searchTermValue)" />
                    <p
                      v-if="item[subLabel]"
                      class="ellipsis"
                      :inner-html.prop="item[subLabel] | boldMatch(searchTermValue)" />
                  </div>
                  <!-- <p class="greedy ellipsis">{{ item[trackBy] ? item[label] : item }}</p> -->
                  <!-- DEFAULT suffix icon -->
                  <SBIcon
                    v-if="showSuffixIcon"
                    class="suffix-icon flex flex-align-center rotate-90"
                    :icon="SBIconList.DOWN_ARROW"
                    :color="SBColors.blackUltralight"
                    :hover-icon-color="SBColors.blackPrimary"
                    :size="8"
                    :hover-size="16" />
                  <!-- DEFAULT suffix tick -->
                  <SBIcon
                    v-if="showSuffixTick && (item[trackBy] || item[trackBy] === 0 ? selectedItems[item[trackBy]] : selectedItems[item])"
                    class="suffix-tick"
                    :icon="SBIconList.DONE_TICK"
                    :color="SBColors.bluePrimary"
                    :size="9" />
                </span>
              </slot>
            </li>
          </ul>
        </template>
        <Spinner
          v-if="showLoader"
          :size="20"
          :thickness="2" />
      </ScrollArea>
    </div>
  </div>
</template>

<script>

import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import ScrollArea from '@/components/common/ScrollArea';
import Spinner from '@/components/common/Spinner';

export default {
  name: 'FilterSingle',
  components: {
    SBIcon,
    ScrollArea,
    Spinner,
  },
  props: {
    groupTitle: { // TODO this has to go aout and groups should be a part of the items.
      type: String,
      required: false,
      default: null,
    },
    asyncMode: { // todo
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Function to filter the items on the list.
     * If no function is provided then defaultFilterFunction will be used.
     */
    filterFn: {
      type: Function,
      required: false,
      default: null,
    },
    /** Items to be populated in te list of options. */
    items: {
      type: Array,
      required: true,
    },
    /** Key to use for the label on item if item is provided as object. */
    label: {
      type: String,
      required: false,
      default: 'label',
    },
    /** Defines items treshold for the search bar to show. */
    minItemsToShowSearch: {
      type: Number,
      required: false,
      default: 4,
    },
    /** Value for the search to be prepopulated with. */
    searchTerm: {
      type: String,
      required: false,
      default: '',
    },
    /** Search input placeholder. */
    searchTermPlaceholder: {
      type: String,
      required: false,
      default: 'Search',
    },
    /** In case of async filter this can force the loader to be shown on the bottom. TODO */
    showLoader: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Flag for showing the prefix icon. */
    showPrefixIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Flag for showing the suffix icon. */
    showSuffixIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Key to use for the sublabel on item if item is provided as object. */
    subLabel: {
      type: String,
      required: false,
      default: 'sublabel',
    },
    /** Title for the dropdown menu. */
    title: {
      type: String,
      required: false,
      default: 'Filter',
    },
    /** Key to use for the value to be included in the selected items response array. */
    trackBy: {
      type: String,
      required: false,
      default: 'name',
    },
    /** Key to use for the item icon on item if item is provided as object. */
    trackIcon: {
      type: String,
      required: false,
      default: null,
    },
    /** Flag for showing the prefix tick if item is selected. */
    showPrefixTick: {
      type: Boolean,
      required: false,
      default: true,
    },
    /** Flag for showing the suffix tick if item is selected. */
    showSuffixTick: {
      type: Boolean,
      required: false,
      default: true,
    },
    /** v-model value bind */
    value: {
      type: Array,
      required: false,
      default: null,
    },
    /**
     * Preffered position - accepts left and right for now.
     */
    // TODO move to different position if it gets out of view
    placement: {
      type: String,
      required: false,
      default: 'left',
    },
  },
  data() {
    return {
      /** Current selection state which resets on close if not saved */
      selectedItems: {},
      /** Saved filters */
      savedSelection: {},
      /** Defines if dropdown menu is hidden or shown */
      filterVisible: false,
      itemHoveredAllowed: true,
      searchTermValue: '',
      hoveredId: null,
      SBIconList,
      SBColors,
    };
  },
  computed: {
    /** List of items filtered by the search term to be shown on the list. */
    searchResults() {
      if (this.searchTermValue && !this.asyncMode) {
        if (this.filterFn) {
          return this.items.filter(this.filterFn(this.searchTermValue));
        }
        return this.items.filter(this.defaultFilterFunction(this.searchTermValue));
      }
      return this.items;
    },
    /** Number of selected items. */
    selectedItemsCount() {
      return Object.keys(this.selectedItems).length;
    },
  },
  watch: {
    /** Triggers when the search iput is changed. */
    searchTermValue() {
      this.searchUsed = true;
      this.hoveredId = 0;
      this.$emit('search-for', this.searchTermValue);
    },
  },
  created() {
    this.searchTermValue = this.searchTerm;
    if (this.value) {
      const preselectValues = {};
      this.value.forEach((key) => { preselectValues[key] = true; });
      this.selectedItems = { ...preselectValues };
      this.savedSelection = { ...preselectValues };
    }
  },
  methods: {
    /** Clears the search input. */
    clearSearchTerm() {
      setTimeout(() => {
        // DO NOT CHANGE THIS
        // For some reason, if this is not wrapped with setTimeout it triggers v-click-outside directive !?
        this.searchTermValue = '';
      }, 0);
    },
    clearFilter() {
      this.selectedItems = [];
      this.saveFilter();
    },
    /** Closes the filter dropdown. */
    closeFilter() {
      this.filterVisible = false;
      // Emmit difference fom selected and saved items if there is any. This happens in case user decides to cancel selection or clicks outside of the dropdown.
      const diff = (a, b) => a.filter(v => !b.includes(v));
      const remove = diff(Object.keys(this.selectedItems), Object.keys(this.savedSelection));
      const reAdd = diff(Object.keys(this.savedSelection), Object.keys(this.selectedItems));
      if (remove.length) {
        this.$emit('items-removed', remove);
      }
      if (reAdd.length) {
        this.$emit('items-readded', reAdd);
      }
      this.selectedItems = { ...this.savedSelection };
    },
    /** Searches through items or through item labels and sublabels if item is provided as an object. */
    defaultFilterFunction(searchTerm) {
      return item => (
        item[this.label]
          ? (
            item[this.label].toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1 ||
            (item[this.subLabel] && item[this.subLabel].toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1)
          ) : item.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
      );
    },
    /** Resets the hovered id which is used for scrolling into the view if needed. */
    deselect() {
      if (this.itemHoveredAllowed) {
        this.hoveredId = null;
      }
    },
    /** TODO used for async mode */
    handleBottomScroll() {},
    /** Triggers the hovered id setup. */
    itemHovered(itemId) {
      if (this.itemHoveredAllowed) {
        this.select(itemId);
      }
    },
    /** Saves the filter selection and emits related events including input required for v-model bind. */
    saveFilter() {
      this.savedSelection = { ...this.selectedItems };
      this.closeFilter();
      this.$emit('item-updated');
      this.$emit('items-selected', Object.keys(this.selectedItems));
      this.$emit('input', Object.keys(this.selectedItems));
    },
    /** Scrolls a list up or down to show hovered list item in full. */
    scrollIntoViewIfNeeded(target) {
      const rectParent = target.parentElement.parentElement.parentElement.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      this.$nextTick(() => {
        const pseudoElementStyle = getComputedStyle(target.parentElement.parentElement.parentElement);
        const borderTopWidth = Number.parseInt(pseudoElementStyle.borderTopWidth.replace('px', ''), 10);
        const topPadding = Number.parseInt(pseudoElementStyle.paddingTop.replace('px', ''), 10);
        const parentTop = rectParent.top + borderTopWidth + topPadding;
        const parentBottom = rectParent.bottom + borderTopWidth + topPadding;
        if (rect.top < parentTop) {
          target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
        } else if (rect.bottom > parentBottom) {
          target.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'smooth' });
        }
      });
    },
    /** Sets the hovered id and initiates scroll into view. */
    select(itemId) {
      this.hoveredId = itemId;
      this.scrollIntoViewIfNeeded(this.$refs.itemListWrapperRef.children[itemId]);
    },
    /**
     * Adds an item to the selected items list and emits related events.
     * NOTE: item-selected event only represents current selection state but not the saved selection state. If the filter is closed without saving then the items-removed and items-readded events will be emitted if needed.
     */
    selectItem(item) {
      const selectedKey = item[this.trackBy] || item[this.trackBy] === 0 ? item[this.trackBy] : item;
      if (this.selectedItems[selectedKey]) {
        delete this.selectedItems[selectedKey];
        this.$emit('item-removed', selectedKey);
      } else {
        this.selectedItems[selectedKey] = true;
        this.$emit('item-selected', selectedKey);
      }

      this.selectedItems = { ...this.selectedItems };
    },
    /** Toggles filter dropdown visibility. */
    toggleFilter() {
      this.filterVisible = !this.filterVisible;
      if (!this.filterVisible) {
        this.closeFilter();
      } else {
        this.$nextTick(() => {
          if (this.$refs.searchTermInputRef) {
            this.$refs.searchTermInputRef.focus();
          }
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#filter-wrapper {
  display: flex;
  position: relative;

  .sb2-bordered {
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px #cfd8dc;
    border-radius: 5px;

    &.sb2-hoverable:hover {
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
    }

    &.sb2-focusable:focus {
      outline: none !important;
      border: solid 1px #cfd8dc;
      box-shadow: 0 0 0 3px #bfdeff !important;
    }
  }

  .filter-button {
    padding-left: 15px !important;
    padding-right: 15px !important;
    height: 30px;
    transition: none;

    &.counter-present {
      padding-right: 8px !important;
    }

    .filter-icon {
      margin-right: 6px;
    }

    .filter-count {
      border-left: 1px solid #cfd8dc;
      margin: 0 0 0 16px;
      padding: 0 0 0 8px;
      display: flex;
      align-items: center;
      font-size: 14px;
      font-weight: 500;
      color: #2680eb;
    }
  }

  .filter-dropdown {
    position: absolute;
    // z-index: 1100;
    // z-index: 100;
    width: 296px;
    height: fit-content;
    max-height: 330px;
    background-color: $white;
    border-radius: 5px;
    box-shadow: 0 2px 5px 0 rgba(13, 15, 22, 0.23);
    overflow: hidden;

    &.left {
      top: 40px;
      left: 10px;
    }

    &.right {
      top: 40px;
      right: 0;
    }

    header {
      padding: 12px;
      border-bottom: 1px solid $greyBorderModal;

      .btn {
        font-size: 12px;
        font-weight: 500;
        max-height: 24px;
        padding: 6px 10px !important;
      }

      .title {
        font-size: 14px;
        font-weight: 500;
      }
    }

    .search-input-wrapper {
      padding: 10px 12px;
      background-color: $greyUltralight;
      border-bottom: solid 1px $greyChatBubble;
      position: relative;

      input {
        font-size: 14px;
        font-weight: 400;
        color: $blackUltralight;
        padding: 4px 30px 4px 8px;
        height: 28px !important;
      }

      .clear-input {
        position: absolute;
        height: 100%;
        top: 0;
        right: 20px;
      }
    }

    .scroll-area {
      .group-title {
        padding: 18px 12px 8px;
        font-size: 12px;
        font-weight: 500;
        color: $greyPrimary;
      }

      ul {
        margin-bottom: 7px;
        li {
          .default-li {
            padding: 10px 12px;
            width: 100%;

            .prefix-icon {
              margin-right: 12px;
            }

            .item-icon-wrapper {
              display: flex;
              align-items: center;
              justify-content: center;
              height: 30px;
              width: 30px;
              border-radius: 5px;
              border: solid 1px #dde6e9;
              background-color: $white;
              margin-right: 8px;

              .item-icon {
                font-size: 11px !important;
                letter-spacing: 2px !important;

                &.FRONTAPP,
                &.FRONTPACHA {
                  background-image: url('~@/assets/img/SB-Icons/integration-front-color.svg');
                  background-size: 14px 14px;
                  background-position: center center;
                  background-repeat: no-repeat;
                  display: block;
                  width: 31px;
                  height: 31px;
                  transform: translateY(0);
                }
              }
            }

            .content {
              width: 100%;

              h3 {
                font-size: 14px;
                font-weight: 400;
                color: $blackPrimary
              }

              p {
                font-size: 11px;
                font-weight: 400;
                color: $blackLight;
              }
            }

            .suffix-icon {
              margin-left: 12px;
            }

            .suffix-tick {
              margin-left: 12px;
            }
          }

          &:hover {
            background-color: $hover;
            cursor: pointer;
          }
        }
      }

      .no-match {
        font-size: 14px;
        padding: 17px 12px;
        // display: flex;
      }
    }
  }
}
</style>
